
import { supabase } from "@/integrations/supabase/client";

// Improved customer notification function with better retry handling
export const sendCustomerNotification = async (
  type: 'booking',
  details: {
    customerName: string;
    customerEmail: string;
    serviceName: string;
    date: string;
    time: string;
    location?: string | null;
    note?: string | null;
    bookingId?: number | string;
    status?: string;
    declineReason?: string | null;
  }
) => {
  // Track start time for performance logging
  const startTime = performance.now();
  
  try {
    console.log(`Sending customer notification for ${type} to ${details.customerEmail}`);
    
    // Track notification attempt with minimal data
    try {
      await supabase.from('test_runs').insert({
        family: 'customer_notification_service',
        type,
        status: 'pending',
        user_email: details.customerEmail,
        user_display_name: details.customerName,
        data: { timestamp: new Date().toISOString() }
      });
    } catch (trackingError) {
      // Just log and continue - tracking shouldn't block the notification
      console.error('Error tracking notification attempt:', trackingError);
    }

    // Set up retry parameters
    const maxRetries = 2; // Reduced from 3 to 2 for faster processing
    const baseDelay = 500; // milliseconds
    
    // Try to send the notification with optimized error handling
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`Retry attempt ${attempt} for customer notification`);
          // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, attempt - 1)));
        }
        
        // Call the edge function with minimal details
        const { data: response, error } = await supabase.functions.invoke('customer-notifications', {
          method: 'POST',
          body: { type, details }
        });
        
        if (error) {
          console.error('Error invoking customer-notifications function:', error);
          continue; // Try again
        }
        
        // Log performance metrics
        const endTime = performance.now();
        console.log(`Customer notification sent in ${Math.round(endTime - startTime)}ms`);
        
        // Track success minimally
        try {
          await supabase.from('test_runs').insert({
            family: 'customer_notification_service',
            type,
            status: response?.success ? 'success' : 'failed',
            user_email: details.customerEmail,
            user_display_name: details.customerName,
            data: {
              duration_ms: Math.round(endTime - startTime),
              timestamp: new Date().toISOString()
            }
          });
        } catch (trackingError) {
          console.error('Error tracking notification result:', trackingError);
        }
        
        return response?.success || false;
      } catch (attemptError) {
        console.error(`Error in notification attempt ${attempt}:`, attemptError);
        // Continue to next retry attempt unless it's the last one
        if (attempt === maxRetries) throw attemptError;
      }
    }
    
    // If we reach here, all retries failed
    throw new Error(`Failed after ${maxRetries} retry attempts`);
    
  } catch (error: any) {
    console.error('Error in sendCustomerNotification:', error);
    
    // Track exception minimally
    try {
      await supabase.from('test_runs').insert({
        family: 'customer_notification_service',
        type,
        status: 'exception',
        user_email: details.customerEmail,
        user_display_name: details.customerName,
        data: {
          error_message: error.message,
          timestamp: new Date().toISOString()
        }
      });
    } catch (trackingError) {
      console.error('Error tracking notification exception:', trackingError);
    }
    
    return false;
  }
};
